import { Component, escapeHtml, register, uid } from './core.js';
import { iconHtml } from './icons.js';
import { cx, pick, attrs, mountRoot, bindActions } from './chrome.js';
import { buttonHtml } from './button.js';

const TONES = ['default', 'info', 'success', 'warning', 'danger'];
const DEFAULTS = { title: '', subtitle: '', icon: null, actions: [], body: '', bodyHtml: '', footer: '', footerHtml: '', tone: 'default', padding: 'md', ariaLabel: null, onAction: null };

const isNode = (v) => typeof Node !== 'undefined' && v instanceof Node;

/** Shared markup builder for Card (live and static). Node bodies leave an empty slot the live component fills. */
export function cardHtml(opts) {
	const o = Object.assign({}, DEFAULTS, opts);
	const id = o.id || uid('dfc-card');
	const titleId = id + '-title';
	const actions = Array.isArray(o.actions) ? o.actions : [];
	const hasHead = o.title || o.subtitle || o.icon || actions.length;
	const cls = cx('dfc', 'dfc-card', 'dfc-tone-' + pick(o.tone, TONES, 'default'), 'dfc-pad-' + pick(o.padding, ['none', 'sm', 'md'], 'md'), o.className);
	let head = '';
	if (hasHead) {
		head = '<div class="dfc-card-head">'
			+ iconHtml(o.icon, 'dfc-card-icon')
			+ '<div class="dfc-card-heading">'
			+ (o.title ? `<div class="dfc-card-title" id="${escapeHtml(titleId)}">${escapeHtml(o.title)}</div>` : '')
			+ (o.subtitle ? `<div class="dfc-card-subtitle">${escapeHtml(o.subtitle)}</div>` : '')
			+ '</div>'
			+ (actions.length ? `<div class="dfc-card-actions">${actions.map((a) => buttonHtml({ label: a.label, icon: a.icon, variant: 'ghost', size: 'sm', action: a.id })).join('')}</div>` : '')
			+ '</div>';
	}
	let body = '';
	if (isNode(o.body)) body = '<div class="dfc-card-body" data-dfc-slot="body"></div>';
	else if (o.bodyHtml) body = `<div class="dfc-card-body">${o.bodyHtml}</div>`;
	else if (o.body !== '' && o.body !== null && o.body !== undefined) body = `<div class="dfc-card-body">${escapeHtml(o.body)}</div>`;
	let foot = '';
	if (isNode(o.footer)) foot = '<div class="dfc-card-footer" data-dfc-slot="footer"></div>';
	else if (o.footerHtml) foot = `<div class="dfc-card-footer">${o.footerHtml}</div>`;
	else if (o.footer) foot = `<div class="dfc-card-footer">${escapeHtml(o.footer)}</div>`;
	return `<section${attrs({ class: cls, 'aria-labelledby': o.title ? titleId : null, 'aria-label': !o.title && o.ariaLabel ? o.ariaLabel : null })}>${head}${body}${foot}</section>`;
}

/**
 * Card: a light surface with an optional header (icon, title, subtitle, actions), body and footer.
 *
 * new dfc.Card(container, { title, subtitle, icon, actions: [{ id, label, icon }],
 *   body: string | Node, bodyHtml (trusted HTML), footer: string | Node, footerHtml,
 *   tone: 'default' | 'info' | 'success' | 'warning' | 'danger', padding: 'none' | 'sm' | 'md', onAction })
 * Events: action { id, event }
 * Members: bodyEl, footerEl
 * Static: dfc.Card.renderStatic(opts) -> HTML string (Node bodies are not available there)
 */
export class Card extends Component {
	static get componentName() { return 'card'; }
	defaults() { return Object.assign({}, DEFAULTS); }

	render() {
		const el = mountRoot(this, cardHtml(Object.assign({}, this.options, { id: this.id })));
		for (const part of ['body', 'footer']) {
			const slot = el.querySelector(`[data-dfc-slot="${part}"]`);
			if (slot) slot.appendChild(this.options[part]);
		}
		this.bodyEl = el.querySelector('.dfc-card-body');
		this.footerEl = el.querySelector('.dfc-card-footer');
		bindActions(this);
	}

	static renderStatic(opts = {}) { return cardHtml(opts); }
}

register('card', Card);
